import { useEffect, useState } from "react";
import {
  Button,
  Card,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconDownload } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../../api/client";
import type { WorkersReportSummary } from "../../api/types";
import { ResponsiveTable } from "../responsiveTable/ResponsiveTable";

function firstOfMonthStr(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}-01`;
}

function endOfMonthStr(): string {
  const d = new Date();
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const y = last.getFullYear();
  const m = String(last.getMonth() + 1).padStart(2, "0");
  const dd = String(last.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

export function ReportTab() {
  const { t } = useTranslation();
  const [from, setFrom] = useState<string>(firstOfMonthStr());
  const [to, setTo] = useState<string>(endOfMonthStr());
  const [report, setReport] = useState<WorkersReportSummary | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!from || !to) return;
    let cancelled = false;
    setLoading(true);
    api
      .workersReportSummary(from, to)
      .then((r) => {
        if (!cancelled) setReport(r);
      })
      .catch((e) => {
        if (cancelled) return;
        notifications.show({ color: "red", message: e instanceof ApiError ? JSON.stringify(e.detail) : String(e) });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // `t` omitted intentionally: stable, and including it would refetch on language change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  function downloadXlsx() {
    if (!from || !to) return;
    const url = api.workersReportExportUrl(from, to);
    // Trigger a browser download — auth cookie travels with the request
    window.location.href = url;
  }

  return (
    <Stack>
      <Group align="end" gap="sm" wrap="wrap">
        <TextInput
          type="date"
          label={t("workers.report.from")}
          value={from}
          onChange={(e) => setFrom(e.currentTarget.value)}
          w={160}
        />
        <TextInput
          type="date"
          label={t("workers.report.to")}
          value={to}
          onChange={(e) => setTo(e.currentTarget.value)}
          w={160}
        />
        {report && (
          <Button variant="default" leftSection={<IconDownload size={14} />} onClick={downloadXlsx}>
            {t("workers.report.exportXlsx")}
          </Button>
        )}
      </Group>

      {loading && <Loader />}

      {report && !loading && (
        <Stack gap="md">
          <Card withBorder>
            <Title order={5} mb="xs">{t("workers.report.grandTotal")}</Title>
            <SimpleGrid cols={{ base: 2, sm: 5 }}>
              <Stat label={t("workers.report.days")} value={String(report.grand_total.days)} />
              <Stat label={t("workers.report.hours")} value={report.grand_total.hours.toFixed(2)} />
              <Stat label={t("workers.report.earned")} value={report.grand_total.earned.toFixed(2)} />
              <Stat label={t("workers.report.paid")} value={report.grand_total.paid.toFixed(2)} />
              <Stat
                label={t("workers.report.owed")}
                value={report.grand_total.owed.toFixed(2)}
                color={report.grand_total.owed > 0 ? "orange" : "teal"}
              />
            </SimpleGrid>
          </Card>

          <Card withBorder>
            <Title order={5} mb="xs">{t("workers.report.perWorker")}</Title>
            <ResponsiveTable
              forceMode="sticky"
              mobile={{
                rows: report.per_worker,
                rowKey: (r) => r.worker_id,
                title: (r) => r.full_name,
                subtitle: (r) => r.role_name ?? "—",
                fields: [
                  { label: t("workers.report.days"), render: (r) => r.days },
                  { label: t("workers.report.hours"), render: (r) => r.hours.toFixed(2) },
                  { label: t("workers.report.earned"), render: (r) => r.earned.toFixed(2) },
                  { label: t("workers.report.paid"), render: (r) => r.paid.toFixed(2) },
                  { label: t("workers.report.owed"), render: (r) => <Text span c={r.owed > 0 ? "orange" : "dimmed"}>{r.owed.toFixed(2)}</Text> },
                ],
              }}
            >
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("workers.col.name")}</Table.Th>
                  <Table.Th>{t("workers.col.role")}</Table.Th>
                  <Table.Th>{t("workers.report.days")}</Table.Th>
                  <Table.Th>{t("workers.report.hours")}</Table.Th>
                  <Table.Th>{t("workers.report.earned")}</Table.Th>
                  <Table.Th>{t("workers.report.paid")}</Table.Th>
                  <Table.Th>{t("workers.report.owed")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {report.per_worker.map((r) => (
                  <Table.Tr key={r.worker_id}>
                    <Table.Td>{r.full_name}</Table.Td>
                    <Table.Td>{r.role_name ?? "—"}</Table.Td>
                    <Table.Td>{r.days}</Table.Td>
                    <Table.Td>{r.hours.toFixed(2)}</Table.Td>
                    <Table.Td>{r.earned.toFixed(2)}</Table.Td>
                    <Table.Td>{r.paid.toFixed(2)}</Table.Td>
                    <Table.Td>
                      <Text c={r.owed > 0 ? "orange" : "dimmed"}>{r.owed.toFixed(2)}</Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            </ResponsiveTable>
          </Card>

          <Card withBorder>
            <Title order={5} mb="xs">{t("workers.report.perRole")}</Title>
            <ResponsiveTable
              mobile={{
                rows: report.per_role,
                rowKey: (r) => String(r.role_id),
                title: (r) => r.role_name ?? "—",
                fields: [
                  { label: t("workers.report.days"), render: (r) => r.days },
                  { label: t("workers.report.hours"), render: (r) => r.hours.toFixed(2) },
                  { label: t("workers.report.earned"), render: (r) => r.earned.toFixed(2) },
                ],
              }}
            >
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("workers.col.role")}</Table.Th>
                  <Table.Th>{t("workers.report.days")}</Table.Th>
                  <Table.Th>{t("workers.report.hours")}</Table.Th>
                  <Table.Th>{t("workers.report.earned")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {report.per_role.map((r) => (
                  <Table.Tr key={String(r.role_id)}>
                    <Table.Td>{r.role_name ?? "—"}</Table.Td>
                    <Table.Td>{r.days}</Table.Td>
                    <Table.Td>{r.hours.toFixed(2)}</Table.Td>
                    <Table.Td>{r.earned.toFixed(2)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            </ResponsiveTable>
          </Card>
        </Stack>
      )}
    </Stack>
  );
}

function Stat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <Stack gap={2}>
      <Text size="xs" c="dimmed">{label}</Text>
      <Text fw={600} c={color}>{value}</Text>
    </Stack>
  );
}
